import { Link } from 'react-router-dom'
import PolicyPage, { PolicySection, PolicyList } from 'components/compliance/policy-page'
import compliance from 'data/compliance.json'

const { professionalDevelopment, certifications } = compliance
const britishCouncil = certifications.find((item) => item.name === 'British Council certificate')
const iso = certifications.find((item) => item.name === 'ISO certification')
const namedProviders = professionalDevelopment.namedProviders.value

function ProfessionalStandards() {
  return (
    <PolicyPage
      title='Professional Standards and Staff Development'
      description='How UHDSS keeps the knowledge of its counsellors up to date, and what our certifications do and do not cover.'
      intro='Counselling students well depends on current knowledge. This page explains how we keep our staff up to date, and describes the qualifications shown on this website accurately.'
    >
      <PolicySection title='Our commitment to continuing professional development'>
        <p className='dark-blue fs-6'>
          UHDSS is committed to maintaining and improving the professional knowledge of the staff who
          counsel students and handle applications. Entry requirements, admissions processes and
          immigration guidance change often, and advice that was correct last year may not be correct
          today. Our staff development covers areas such as:
        </p>
        <PolicyList items={professionalDevelopment.areas} />
      </PolicySection>

      <PolicySection title='How we record it'>
        <p className='dark-blue fs-6'>
          We keep an internal professional development record for the staff involved in counselling
          and recruitment. For each activity it shows:
        </p>
        <PolicyList items={professionalDevelopment.recordKeeping} />
        <p className='dark-blue fs-6'>
          {namedProviders.length > 0 ? (
            `We take part in training from: ${namedProviders.join(', ')}.`
          ) : (
            'We do not list named training providers on this page. Where a member of staff completes a recognised programme, the certificate and the provider are recorded internally, and we can show that record on request during a due diligence review.'
          )}
        </p>
      </PolicySection>

      <PolicySection title='What our certifications cover'>
        <p className='dark-blue fs-6'>
          Some qualifications belong to an individual counsellor rather than to UHDSS as an
          organisation. We describe them as what they are, and we do not present an individual
          certificate as accreditation or endorsement of the company.
        </p>
        {iso && (
          <p className='dark-blue fs-6'>
            <strong>{iso.standard}: </strong>
            {iso.holder.value} holds a {iso.system} certificate to {iso.standard}, certificate number{' '}
            {iso.certificateNumber}, issued by {iso.issuingBody} on {iso.issueDate} and valid until{' '}
            {iso.expiryDate}. Its scope is: {iso.certificateScope.toLowerCase()}. You can check its
            status at{' '}
            <a className='text-decoration-none' href={iso.verificationUrl} target='_blank' rel='noopener noreferrer'>
              {iso.verificationUrl}
            </a>. This certificate is about how we manage the quality of our own work. It says
            nothing about the outcome of any application.
          </p>
        )}
        {britishCouncil && (
          <p className='dark-blue fs-6'>
            <strong>British Council certificate: </strong>
            held by {britishCouncil.holder.value}, {britishCouncil.holder.role} of UHDSS.{' '}
            {britishCouncil.description}
          </p>
        )}
        <p className='dark-blue fs-6'>
          Where we show a certificate on this website, the image of the certificate is published so
          that you can read what it says for yourself.
        </p>
      </PolicySection>

      <PolicySection title='How this affects the advice you receive'>
        <p className='dark-blue fs-6'>
          Training helps us give you better information, but it does not give us influence over any
          decision. Admission, scholarship and visa decisions are made by institutions and
          government authorities, as explained in our{' '}
          <Link className='text-decoration-none' to='/admissions-disclaimer'>admissions, scholarship and visa disclaimer</Link>.
          Where the requirements matter to your case, we will point you to the official source.
        </p>
      </PolicySection>
    </PolicyPage>
  )
}

export default ProfessionalStandards
